export const STUDENT_TRAINING_MODE_MEANING = 'meaning';
export const STUDENT_TRAINING_MODE_COMPREHENSIVE = 'comprehensive';

export const STUDENT_TRAINING_MODES = Object.freeze([
  STUDENT_TRAINING_MODE_MEANING,
  STUDENT_TRAINING_MODE_COMPREHENSIVE,
]);

const PRIMARY_SENSE_ID = 'primary';

export const normalizeStudentWord = (value) => String(value ?? '').trim().toLowerCase();

export const normalizeStudentSenseId = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized || PRIMARY_SENSE_ID;
};

export const normalizeStudentTrainingMode = (value) => (
  String(value ?? '').trim().toLowerCase() === STUDENT_TRAINING_MODE_COMPREHENSIVE
    ? STUDENT_TRAINING_MODE_COMPREHENSIVE
    : STUDENT_TRAINING_MODE_MEANING
);

const orderedModes = (values) => STUDENT_TRAINING_MODES.filter((mode) => values.includes(mode));

const uniqueModes = (values) => orderedModes([
  ...new Set((values || []).map(normalizeStudentTrainingMode)),
]);

const modeValues = (record, fields) => {
  const values = [];
  fields.forEach((field) => {
    const value = record?.[field];
    if (Array.isArray(value)) values.push(...value);
    else if (value !== undefined && value !== null && String(value).trim()) values.push(value);
  });
  return uniqueModes(values);
};

const recordModes = (record) => modeValues(record, [
  'training_modes',
  'target_training_modes',
  'modes',
  'training_mode',
  'trainingMode',
]);

const completedRecordModes = (record) => modeValues(record, [
  'completed_training_modes',
  'completed_modes',
]);

const pendingRecordModes = (record) => modeValues(record, [
  'pending_training_modes',
  'pending_modes',
]);

const isCompletedRecord = (record) => (
  record?.completed === true
  || record?.learned === true
  || record?.mastered === true
  || ['completed', 'learned', 'mastered'].includes(String(record?.status || '').trim().toLowerCase())
);

const isPendingRecord = (record, defaultPending) => {
  if (record?.pending === true || record?.pending === 'true') return true;
  if (record?.pending === false || record?.pending === 'false') return false;
  return Boolean(defaultPending) && !isCompletedRecord(record);
};

const firstText = (...values) => values.find((value) => (
  value !== undefined && value !== null && String(value).trim()
)) || '';

const recordDefinition = (record, language) => {
  const sense = record?.sense && typeof record.sense === 'object' ? record.sense : {};
  if (language === 'cn') {
    return firstText(
      record?.definition_cn,
      record?.meaning_cn,
      record?.chinese_definition,
      sense.definition_cn,
      sense.meaning_cn,
    );
  }
  return firstText(
    record?.definition_en,
    record?.meaning_en,
    record?.english_definition,
    sense.definition_en,
    sense.meaning_en,
  );
};

const addMode = (target, mode) => {
  if (!target.includes(mode)) target.push(mode);
};

/**
 * Merge history or pending rows by the canonical student-facing sense identity.
 * Completion is intentionally explicit: `pending: false` alone can mean that a
 * teacher cleared a task, so it must never manufacture a Learned row.
 */
export const groupStudentVocabularyModes = (items, { defaultPending = false } = {}) => {
  const groups = new Map();
  (Array.isArray(items) ? items : []).forEach((rawItem, index) => {
    const record = typeof rawItem === 'string' ? { word: rawItem } : rawItem;
    if (!record || typeof record !== 'object') return;
    const word = String(record.word ?? record.spelling ?? '').trim();
    const normalizedWord = normalizeStudentWord(word);
    if (!normalizedWord) return;
    const senseId = normalizeStudentSenseId(record.sense_id ?? record.senseId);
    const key = `${normalizedWord}::${senseId}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        normalizedWord,
        word,
        sense_id: senseId,
        definition_cn: '',
        definition_en: '',
        pos: firstText(record.pos, record.sense?.pos),
        pendingModes: [],
        completedModes: [],
        modes: [],
        pending: false,
        completed: false,
        firstIndex: index,
      });
    }
    const group = groups.get(key);
    if (!group.definition_cn) group.definition_cn = recordDefinition(record, 'cn');
    if (!group.definition_en) group.definition_en = recordDefinition(record, 'en');
    if (!group.pos) group.pos = firstText(record.pos, record.sense?.pos);

    const rowModes = pendingRecordModes(record).length
      ? pendingRecordModes(record)
      : (recordModes(record).length ? recordModes(record) : [STUDENT_TRAINING_MODE_MEANING]);
    const pending = isPendingRecord(record, defaultPending);
    if (pending) rowModes.forEach((mode) => addMode(group.pendingModes, mode));

    const completedModes = completedRecordModes(record);
    if (completedModes.length) {
      completedModes.forEach((mode) => addMode(group.completedModes, mode));
    } else if (isCompletedRecord(record)) {
      rowModes.forEach((mode) => addMode(group.completedModes, mode));
    }
  });

  return [...groups.values()].map((group) => {
    const pendingModes = orderedModes(group.pendingModes);
    const completedModes = orderedModes(group.completedModes);
    const modes = orderedModes([...new Set([...completedModes, ...pendingModes])]);
    return {
      ...group,
      pendingModes,
      completedModes,
      modes,
      pending: pendingModes.length > 0,
      completed: completedModes.length > 0,
    };
  });
};

export const extractOverviewPendingItems = (overview) => {
  const structuredKeys = [
    'teacher_pending_items',
    'pending_teacher_items',
    'tbm_teacher_assigned_items',
    'pending_items',
  ];
  const structured = structuredKeys
    .map((key) => overview?.[key])
    .find((value) => Array.isArray(value) && value.length > 0);
  if (structured) return structured;
  const emptyStructured = structuredKeys
    .map((key) => overview?.[key])
    .find((value) => Array.isArray(value));
  if (emptyStructured) return emptyStructured;
  return (Array.isArray(overview?.tbm_teacher_assigned) ? overview.tbm_teacher_assigned : [])
    .map((word) => ({ word, sense_id: PRIMARY_SENSE_ID, training_mode: STUDENT_TRAINING_MODE_MEANING, pending: true }));
};

const addHistoryRows = (target, rows, state) => {
  if (!Array.isArray(rows)) return;
  rows.forEach((item) => {
    if (typeof item === 'string') {
      target.push({ word: item, ...state });
      return;
    }
    if (!item || typeof item !== 'object') return;
    const next = { ...item };
    if (Object.prototype.hasOwnProperty.call(state, 'pending') && next.pending === undefined) {
      next.pending = state.pending;
    }
    if (Object.prototype.hasOwnProperty.call(state, 'completed') && next.completed === undefined) {
      next.completed = state.completed;
    }
    target.push(next);
  });
};

export const extractHistoryItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const rows = [];
  addHistoryRows(rows, payload.vocab_mission, {});
  if (!rows.length) addHistoryRows(rows, payload.history, {});
  if (!rows.length) addHistoryRows(rows, payload.items, {});
  addHistoryRows(rows, payload.pending_items, { pending: true });
  addHistoryRows(rows, payload.completed_items, { completed: true, pending: false });
  return rows;
};

export const buildTrainingModeEditItems = (groups) => (Array.isArray(groups) ? groups : [])
  .filter((group) => group?.pending)
  .map((group) => ({
    word: group.word,
    sense_id: normalizeStudentSenseId(group.sense_id),
    // Completed modes describe history and must never be written back as a
    // fresh assignment. The manager edits only the still-pending tracks.
    training_modes: uniqueModes(group.pendingModes),
  }))
  .filter((item) => item.word && item.training_modes.length > 0);

export const hasTrainingMode = (group, mode) => (
  uniqueModes(group?.modes).includes(normalizeStudentTrainingMode(mode))
);
